import React, { useState } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { testimonials } from '../utils/mockData';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const nextTestimonial = () => setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  const prevTestimonial = () => setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section
        className="relative bg-cover bg-center py-12"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 71, 171, 0.9), rgba(0, 71, 171, 0.9)), url(https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1200)',
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Client Testimonials</h1>
          <p className="text-lg md:text-xl max-w-3xl mx-auto">Hear from families we&apos;ve helped protect with purposeful insurance coverage</p>
        </div>
      </section>

      {/* Featured Carousel */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-2xl p-8 md:p-12">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-bold text-brand-blue">What Our Clients Say</h2>
                <div className="flex gap-2">
                  <button onClick={prevTestimonial} className="p-3 rounded-full bg-gray-100 hover:bg-brand-blue hover:text-white transition" data-testid="testimonial-prev">
                    <ChevronLeft size={24} />
                  </button>
                  <button onClick={nextTestimonial} className="p-3 rounded-full bg-gray-100 hover:bg-brand-blue hover:text-white transition" data-testid="testimonial-next">
                    <ChevronRight size={24} />
                  </button>
                </div>
              </div>
              <div className="text-center">
                <img src={testimonials[currentIndex].image} alt={testimonials[currentIndex].name} className="w-32 h-32 rounded-full mx-auto mb-6 object-cover border-4 border-brand-blue" />
                <div className="flex justify-center gap-2 mb-4">
                  {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                    <Star key={i} size={28} className="text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                {testimonials[currentIndex].category && (
                  <span className="inline-block px-3 py-1 bg-brand-blue/10 text-brand-blue text-xs font-semibold rounded-full mb-4">
                    {testimonials[currentIndex].category}
                  </span>
                )}
                <p className="text-gray-700 italic text-lg mb-8 leading-relaxed">
                  &ldquo;{testimonials[currentIndex].text}&rdquo;
                </p>
                <h3 className="font-bold text-2xl text-brand-blue">{testimonials[currentIndex].name}</h3>
                <p className="text-base text-gray-600">{testimonials[currentIndex].role}</p>
                <p className="text-gray-500">{testimonials[currentIndex].location}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* All Testimonials Grid */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-12 text-center">More Success Stories</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-white rounded-lg shadow-md p-6" data-testid={`testimonial-card-${testimonial.id}`}>
                <div className="flex items-center gap-4 mb-4">
                  <img src={testimonial.image} alt={testimonial.name} className="w-16 h-16 rounded-full object-cover" />
                  <div>
                    <h3 className="font-bold text-brand-blue">{testimonial.name}</h3>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                    {testimonial.category && (
                      <span className="inline-block px-2 py-0.5 bg-brand-blue/10 text-brand-blue text-xs font-semibold rounded-full mt-1">
                        {testimonial.category}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} className="text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 italic text-sm leading-relaxed">&ldquo;{testimonial.text}&rdquo;</p>
                <p className="text-xs text-gray-500 mt-3">{testimonial.location}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-8 bg-brand-blue text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Experience Purposeful Coverage?</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Join the families who trust Purposefully Covered Insurance Group to protect what matters most.
          </p>
          <a href="/#quote" className="inline-block bg-brand-orange text-white px-8 py-4 rounded-md hover:bg-brand-orange-light transition font-semibold text-lg" data-testid="testimonials-quote-btn">
            Get My Free Quote
          </a>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;
